/**
 * The workspace home: what is in this workspace, and what can be started here.
 *
 * The workspace is the unit of everything in M2 — dossiers, runs, data mode,
 * audit — so it is also the unit of the URL (`/workspace/:id`). Switching
 * workspace is navigation, not hidden state: a founder can link a colleague
 * straight into the workspace they mean.
 */
import { useState } from 'react'
import { ClipboardList, History, Library, Upload } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { LoadingBlock } from '@/components/LoadingBlock'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useSession } from '@/features/auth/SessionContext'
import { DeleteDossierButton } from '@/features/retention/DeleteDossierButton'
import { RetentionBadge } from '@/features/retention/RetentionBadge'
import { DemoDossiers } from '@/features/start/DemoDossiers'
import { GemeindeCoverage } from '@/features/start/GemeindeCoverage'
import { DataModeBadge, DataModeCard } from '@/features/workspace/DataModeCard'
import { InvitePanel } from '@/features/workspace/InvitePanel'
import { RetentionWindowCard } from '@/features/workspace/RetentionWindowCard'
import { useI18n } from '@/i18n'
import { bautypLabel, roleLabel, verfahrenLabel } from '@/i18n/enumLabels'
import { buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type { DossierRef, WorkspaceRef } from '@/api/types'

export function WorkspaceScreen({ workspace }: { workspace: WorkspaceRef }) {
  const { t } = useI18n()
  const { isFounder, refresh } = useSession()
  const dossiers = useAsync<DossierRef[]>(
    (signal) => api.dossiers(workspace.id, signal),
    [workspace.id],
  )
  const canAdminister = isFounder || workspace.role === 'admin' || workspace.role === 'founder'

  // The dossier list sits across the fold, so filling it in after the rest of
  // the page has painted pushes everything below it down — 0.099 CLS, the last
  // surface in the product still shifting. One state change instead: wait for
  // the list, then paint the page. Only the *first* paint waits; a reload after
  // a deletion or a retry keeps the page where it is (adjusting state during
  // render, not in an effect — React re-runs immediately, nothing sees stale).
  const [everSettled, setEverSettled] = useState(false)
  if (!dossiers.loading && !everSettled) setEverSettled(true)
  if (dossiers.loading && !everSettled) return <LoadingBlock />

  const openRun = (runId: string) => navigate(buildPath('run', { runId }))

  const startRun = async (dossierId: string) => {
    const run = await api.createRun(dossierId, workspace.id)
    openRun(run.run_id)
  }

  return (
    <div className="flex flex-col gap-12">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">{workspace.name}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <DataModeBadge workspace={workspace} />
          {workspace.role ? (
            <Badge variant="neutral">
              {t('workspace.role')}: {roleLabel(workspace.role, t)}
            </Badge>
          ) : null}
        </div>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('start.lead')}</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => navigate(buildPath('upload', { workspaceId: workspace.id }))}>
            <Icon icon={Upload} />
            {t('workspace.uploadCta')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(buildPath('rulebase', { workspaceId: workspace.id }))}
          >
            <Icon icon={Library} />
            {t('nav.rulebase')}
          </Button>
        </div>
      </section>

      <section aria-labelledby="workspace-dossiers" className="flex flex-col gap-4">
        <div>
          <h2 id="workspace-dossiers" className="font-display text-[21px] font-semibold">
            {t('workspace.dossiersTitle')}
          </h2>
          <p className="mt-1 text-[13px] text-muted">{t('workspace.dossiersLead')}</p>
          {/* Every card below carries its own `delete_after`. This is the
              workspace value those dates were computed from — without it a
              reader cannot tell a short window from a nearly-expired dossier. */}
          <p className="mt-1 text-[13px] text-muted" data-testid="workspace-window">
            {t('retention.window', { days: workspace.retention_days })}
          </p>
        </div>

        {dossiers.error ? (
          <ErrorNote message={dossiers.error} onRetry={dossiers.reload} retryLabel={t('common.retry')} />
        ) : null}

        {dossiers.data?.length === 0 ? (
          <p className="text-[13px] text-muted">{t('workspace.dossiersEmpty')}</p>
        ) : null}

        <ul className="flex flex-col gap-3" data-testid="workspace-dossiers">
          {(dossiers.data ?? []).map((dossier) => (
            <li key={dossier.id}>
              <Card>
                <CardHeader>
                  <CardTitle>{dossier.title}</CardTitle>
                  <CardDescription>
                    {dossier.gemeinde ?? t('common.none')} ·{' '}
                    {bautypLabel(dossier.bautyp, t) ?? t('common.none')} ·{' '}
                    {verfahrenLabel(dossier.verfahren, t) ?? t('common.none')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-3">
                  <Badge variant="neutral">
                    {dossier.kind === 'upload' ? t('workspace.kindUpload') : t('workspace.kindDemo')}
                  </Badge>
                  {dossier.attested ? <Badge variant="outline">{t('workspace.attested')}</Badge> : null}
                  {/* The BRIEF's "shown to the user": the window is on the
                      dossier, not in a settings page nobody opens. */}
                  <RetentionBadge deleteAfter={dossier.delete_after} />
                  <Button size="sm" variant="secondary" onClick={() => void startRun(dossier.id)}>
                    {t('start.startRun')}
                  </Button>
                  {/* The trust surface is reachable from the dossier, not only
                      from a run: a correction outlives the run that produced
                      the value it corrects. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate(
                        buildPath('facts', {
                          workspaceId: workspace.id,
                          dossierId: dossier.id,
                        }),
                      )
                    }
                  >
                    <Icon icon={ClipboardList} />
                    {t('facts.cta')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate(
                        buildPath('dossierAudit', {
                          workspaceId: workspace.id,
                          dossierId: dossier.id,
                        }),
                      )
                    }
                  >
                    <Icon icon={History} />
                    {t('dossierAudit.cta')}
                  </Button>
                  {/* Deleting is the member's own call on their own dossier —
                      it must not need a support ticket. The panel says what
                      will be destroyed before the second press. */}
                  <DeleteDossierButton dossier={dossier} onDeleted={() => dossiers.reload()} />
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <DemoDossiers workspaceId={workspace.id} onRunStarted={(run) => openRun(run.run_id)} />

      <DataModeCard workspace={workspace} canChange={isFounder} onChanged={() => void refresh()} />

      {/* The list is handed to the card so its confirmation can say what a
          shortening does to *these* dossiers — the dates are already on screen,
          so the question needs no endpoint of its own. Reloading it afterwards
          is not cosmetic: a shortening moved the very `delete_after` values the
          badges above are rendering. */}
      <RetentionWindowCard
        workspace={workspace}
        canChange={isFounder}
        dossiers={dossiers.data}
        onChanged={() => {
          void refresh()
          dossiers.reload()
        }}
      />

      {canAdminister ? <InvitePanel workspaceId={workspace.id} /> : null}

      <GemeindeCoverage workspaceId={workspace.id} />
    </div>
  )
}
